import React, { useEffect, useRef, useState } from 'react';
import { ButtonBase, ClickAwayListener, Drawer, Paper, Popper, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { color, r, shadow } from '../../theme/tokens';
import useSoftwareKeyboard from '../../hooks/useSoftwareKeyboard';
import { planMenu, POPOVER_CAP_PX, POPOVER_GAP_PX } from './popoverPlacement';

// A labelled pill in the composer's control row that opens a panel (Aravind,
// 2026-10-09; today only Medications uses it — the goal box became a combobox,
// GoalCombobox.js, whose phone sheet follows the same mechanics):
//   - desktop: a compact popover anchored to its button, about half the
//     composer's width, capped at 23rem tall. It never moves the page: below
//     when the full height fits, otherwise on the side with more room, never
//     squished below a few results (round 7, `planMenu` in
//     popoverPlacement.js);
//   - phone: a full-width bottom sheet with a drag handle, opening at about
//     two-thirds of the screen, growing to full height only while the search
//     keyboard is up.
// The panel inside pins its header (and search) and scrolls its list
// (STACK-565).
//
// `children` is a render prop: `({ close, sheet }) => panel`.

// The panel's header, search and footer plus three or four result rows, at
// the desktop density.
const DEFAULT_MIN_PANEL_PX = 304; // 19rem

export default function ComposerPanelControl({
  minPanelPx = DEFAULT_MIN_PANEL_PX,
  label,
  ariaLabel,
  filled = false,
  onOpen,
  testId,
  children,
}) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const keyboardOpen = useSoftwareKeyboard();
  const [open, setOpen] = useState(false);
  // Desktop: where the popover opens and how tall, planned on each open.
  const [plan, setPlan] = useState({ placement: 'bottom-start', maxHeightPx: POPOVER_CAP_PX });
  const anchorRef = useRef(null);

  const toggle = () => {
    if (open) {
      setOpen(false);
      return;
    }
    if (!phone && anchorRef.current) {
      setPlan(planMenu(anchorRef.current, { minPx: minPanelPx }));
    }
    setOpen(true);
    onOpen?.();
  };
  const close = () => setOpen(false);
  const panel = children({ close, sheet: phone });

  // Escape closes the desktop popover wherever focus is, even once focus has
  // left its search field. The phone sheet is an MUI Drawer, which handles
  // Escape itself.
  useEffect(() => {
    if (!open || phone) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, phone]);

  return (
    <>
      <ButtonBase
        ref={anchorRef}
        onClick={toggle}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={ariaLabel}
        data-testid={testId}
        sx={{
          flex: 'none',
          maxWidth: '100%',
          height: { xs: r(44), sm: r(36) },
          borderRadius: r(999),
          pl: { xs: r(12), sm: r(10) },
          pr: { xs: r(14), sm: r(12) },
          gap: r(6),
          border: `${r(1)} solid ${open ? color.accent : filled ? color.evergreen : color.line}`,
          backgroundColor: filled ? color.mint : color.paper,
          color: color.evergreen,
          fontSize: { xs: r(14), sm: r(13) },
          fontWeight: 600,
          whiteSpace: 'nowrap',
          boxShadow: open ? `0 0 0 ${r(3)} ${alpha(color.accent, 0.18)}` : 'none',
          '&:hover': { borderColor: color.accent, backgroundColor: filled ? color.mint : alpha(color.accent, 0.1) },
        }}
      >
        {label}
      </ButtonBase>

      {phone ? (
        <Drawer
          anchor="bottom"
          open={open}
          onClose={close}
          // Above the app's bottom navigation, which sits at the modal layer
          // (1300) and would otherwise cover the sheet's buttons.
          sx={{ zIndex: theme.zIndex.modal + 1 }}
          PaperProps={{
            sx: {
              borderTopLeftRadius: r(20),
              borderTopRightRadius: r(20),
              height: open && keyboardOpen ? '100dvh' : '65vh',
              transition: 'height 0.2s ease',
              boxShadow: shadow.modal,
            },
          }}
        >
          {panel}
        </Drawer>
      ) : (
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement={plan.placement}
          // Popper's own flip stays off: it flipped above whenever the page
          // was short. `planMenu` decides the side from the room on each.
          modifiers={[
            { name: 'offset', options: { offset: [0, POPOVER_GAP_PX] } },
            { name: 'flip', enabled: false },
          ]}
          sx={{ zIndex: theme.zIndex.modal, width: r(352), maxWidth: 'calc(100vw - 2rem)' }}
        >
          <ClickAwayListener onClickAway={(e) => {
            if (anchorRef.current && anchorRef.current.contains(e.target)) return;
            close();
          }}
          >
            <Paper
              elevation={0}
              sx={{
                border: `${r(1)} solid ${color.line}`,
                borderRadius: r(16),
                boxShadow: shadow.pop,
                overflow: 'hidden',
                maxHeight: `${plan.maxHeightPx / 16}rem`,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {panel}
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </>
  );
}
